import React, { useMemo } from "react";
import { Button, Loading, Typography } from "@kitbird/core";
import { ColumnI } from "../../../../../components/Table/TableProps";
import { ReactComponent as EditIcon } from "../../../../../assets/icons/edit.svg";
import { ReactComponent as TrashIcon } from "../../../../../assets/icons/trash.svg";
import Table from "../../../../../components/Table";
import useBusiness from "../../hooks/useBusiness";
import { IGetBusinessItemData } from "../../../../../services/businessService/types";
import { StickyTable } from "../../../../../components/Table/Table.style";

interface BusinessTableProps {
  useCustomHook: ReturnType<typeof useBusiness>;
  showModal: () => void;
}

const BusinessTable = ({ useCustomHook, showModal }: BusinessTableProps) => {
  const {
    params,
    setParams,
    meta,
    obsRef,
    tableRef,
    setEditValue,
    setIdDeleteModal,
    businessList,
    isFetching,
    isFetchingNextPage,
    areaOpts,
  } = useCustomHook;
  const onClickEdit = (row: IGetBusinessItemData) => {
    const newEditValue = {
      id: row.id,
      area: { label: row.area_name, value: row.area_id },
      pool: { label: row.pool_id, value: row.pool_id },
      segment: { label: row.desc_segment, value: row.code_segment },
      codeType: row.code_type,
      company: { label: row.company_code, value: row.company_code },
      hasRelation: !!row.company_relation,
      companyRelation: { label: row.company_relation, value: row.company_relation },
      isActive: row.is_active,
    };
    setEditValue(newEditValue);
    showModal();
  };
  const onClickDelete = (id: number) => {
    setIdDeleteModal(id);
  };

  const columns: ColumnI[] = useMemo(() => [
    {
      title: "Tipe user",
      dataIndex: "code_type_name",
      key: "code_type_name",
    },
    {
      title: "Wilayah & pool",
      dataIndex: "area_id",
      key: "area_id",
      render: (item, row) => (
        <div style={{ display: "flex", flexDirection: "column" }} >
          <Typography level={6} fontWeight="medium">
            {areaOpts.find((opt) => opt.value === row.area_id)?.label || item}
          </Typography>
          <Typography level={6} fontWeight="regular" color="#666666">
            {row.pool_id}
          </Typography>
        </div>
      ),
    },
    {
      title: "Perusahaan & relasi",
      dataIndex: "company_code",
      key: "company_code",
      sort: true,
      render: (item, row) => (
        <div style={{ display: "flex", flexDirection: "column" }} >
          <Typography level={6} fontWeight="medium">
            {row.company_code}
          </Typography>
          {
            row.company_relation
            && <Typography level={6} fontWeight="regular" color="#666666">
              {row.company_relation}
            </Typography>
          }
        </div>
      ),
    },
    {
      title: "Segmen",
      dataIndex: "desc_segment",
      key: "desc_segment",
    },
    {
      title: "Action",
      dataIndex: "id",
      key: "id",
      render: (item, row) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            style={{ padding: "8px 10px" }}
            onClick={() => onClickEdit(row as IGetBusinessItemData)}
          >
            <EditIcon width={16} height={16} />
          </Button>
          <Button
            style={{ padding: "8px 10px" }}
            backgroundColor={"rgb(203, 91, 75)"}
            onClick={() => onClickDelete(item)}
          >
            <TrashIcon width={16} height={16} />
          </Button>
        </div>
      ),
    },
  ], [areaOpts]);

  return (
    (isFetching && !isFetchingNextPage)
      ? <Loading variant="line" />
      : <StickyTable ref={tableRef}>
        <Table
          withNumber
          columns={columns}
          dataSource={businessList}
          footer={
            <>
              {(!isFetching && meta.page < meta.total_pages) && <tfoot ref={obsRef} style={{ height: 5, width: "100%" }} />}
            </>
          }
          sortConfig={{
            sortType: params.sortType,
            sortBy: params.sortBy,
            onSort: (name, type) => {
              setParams((curr) => ({ ...curr, sortBy: name, sortType: type }));
            },
          }}
        />
        {isFetchingNextPage && <Loading variant="circular" />}
      </StickyTable>
  );
};

export default BusinessTable;
